import React, { useState, useEffect, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { multiply, divide, add } from 'mathjs';
import {
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton,
  useGridApiRef,
  GridRowModes,
} from '@mui/x-data-grid-premium';
import FactoryIcon from '@mui/icons-material/Factory';
import { Alert, Button } from '@mui/material';

import { dataGridPremiumColumns } from '../../utils/helper';
import { ConfirmDialogUI, DataGridIdentifiers, DatagridPremiumUi, InputUI } from '../Interface';
import prodgeneratorService from '../../service/prodgenerator.service';
import { productionGeneratorGroupStatuses } from './ProductionGeneratorGroupStatus';
import { productionGeneratorGridSx } from './ProductionGenerator.style';
import useUsers from '../../context/UsersContext';
import useConfig from '../../context/ConfigContext';
import { useLoader } from '../../provider/LoaderProvider';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

const sourceKey = (row) => `${row.baseDocType}/${row.baseId}/${row.baseLine}`;

const ProductionGeneratorGroupNewProdTable = ({
  removedItemState,
  selectedItemsState,
  selectedRowsState,
  treeDataState,
  sourceDataState,
  setGroupSourceState,
  refreshOrderRows,
}) => {
  const apiRef = useGridApiRef();
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();
  const { showLoader, hideLoader, beginLoading } = useLoader();

  const { removedItem, setRemovedItem } = removedItemState;
  const { selectedItems, setSelectedItems } = selectedItemsState;
  const { selectedRows, setSelectedRows } = selectedRowsState;
  const { setTreeData } = treeDataState;
  const { setSourceData } = sourceDataState;

  const [newProds, setNewProds] = useState([]);
  const [pendingProds, setPendingProds] = useState([]);
  const buildingSources = useRef(new Set());
  const unavailableSources = useRef(new Set());
  const selectedRowIds = useRef(new Set());
  selectedRowIds.current = new Set(selectedRows);
  const lastPendingGroups = useRef(new Map());
  const refreshingSources = useRef(new Map());
  unavailableSources.current = new Set([
    ...[...newProds, ...pendingProds].map(sourceKey), ...refreshingSources.current.keys(),
  ]);
  const queueRevision = useRef(0);
  const groupPayloadCache = useRef(new Map());
  const [pollRevision, setPollRevision] = useState(0);
  const [pollError, setPollError] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const pendingSources = new Set(pendingProds.map(sourceKey));
  const draftProds = newProds.filter((row) => !pendingSources.has(sourceKey(row)));
  const rows = [...draftProds, ...pendingProds];
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const submission = useRef(null);
  const submitting = useRef(false);
  const [groupSubmitting, setGroupSubmitting] = useState(false);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();
  }, []);

  useEffect(() => {
    let disposed = false;
    let timer;
    const controller = new AbortController();
    const refresh = async () => {
      try {
        const revision = queueRevision.current;
        const groups = await prodgeneratorService.getGroupRows(controller.signal, groupPayloadCache.current);
        if (disposed || revision !== queueRevision.current) return;
        const currentGroups = new Map(groups.map((group) => [group.id, group]));
        for (const [id, group] of lastPendingGroups.current) {
          if (!currentGroups.has(id)) refreshingSources.current.set(sourceKey(group), {
            baseDocType: group.baseDocType, baseId: group.baseId, baseLine: group.baseLine,
          });
        }
        lastPendingGroups.current = currentGroups;
        const pending = groups.flatMap((group) => {
          const payload = typeof group.payload === 'string' ? JSON.parse(group.payload) : group.payload;
          return payload.map((row) => ({ ...row, processingGroup: group }));
        });
        setPendingProds(pending);
        const sourceKeys = new Set([...groups.map(sourceKey), ...refreshingSources.current.keys()]);
        setGroupSourceState((previous) => previous.ready
          && previous.sourceKeys.size === sourceKeys.size
          && [...sourceKeys].every((key) => previous.sourceKeys.has(key))
          ? previous : { ready: true, sourceKeys });
        if (refreshingSources.current.size) {
          const sourcesToRefresh = [...refreshingSources.current.values()];
          await refreshOrderRows(sourcesToRefresh, controller.signal);
          if (disposed) return;
          sourcesToRefresh.forEach((source) => refreshingSources.current.delete(sourceKey(source)));
          setGroupSourceState({ ready: true, sourceKeys: new Set([...lastPendingGroups.current.values()].map(sourceKey)) });
        }
        const sources = new Set(pending.map(sourceKey));
        setNewProds((previous) => previous.filter((row) => !sources.has(sourceKey(row))));
        setPollError(false);
        timer = setTimeout(refresh, groups.some((group) => ['WAITING', 'PROCESSING'].includes(group.status)) ? 1000 : 3000);
      } catch {
        if (!disposed) {
          setPollError(true);
          timer = setTimeout(refresh, 1000);
        }
      }
    };
    refresh();
    return () => { disposed = true; clearTimeout(timer); controller.abort(); };
  }, [pollRevision, setGroupSourceState, refreshOrderRows]);

  const retryGroup = async (id) => {
    if (retrying) return;
    setRetrying(id);
    try {
      await prodgeneratorService.retryGroup(id);
      setPollRevision((value) => value + 1);
    } catch {
      // The shared request handler displays the error.
    } finally { setRetrying(null); }
  };

  const clearEditRows = () => {
    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);
    for (const rowId of rowsInEditMode) {
      apiRef.current.stopRowEditMode({ id: rowId });
    }
  };

  useEffect(() => {
    if (removedItem) {
      setNewProds((prev) =>
        prev.filter((prod) => {
          return (
            prod.baseDocType !== removedItem.docTypeId ||
            prod.baseId !== removedItem.docId ||
            prod.baseLine !== removedItem.lineNum
          );
        })
      );

      clearEditRows();
    }
  }, [removedItem]);

  const createRows = async () => {
    if (!selectedItems.length) return;
    const sourceItems = selectedItems.filter((item) => {
      const key = `${item.docTypeId}/${item.docId}/${item.lineNum}`;
      if (buildingSources.current.has(key) || unavailableSources.current.has(key)) return false;
      buildingSources.current.add(key);
      return true;
    });
    if (!sourceItems.length) return;
    const finishLoading = beginLoading();
    try {

    const itemCodes = sourceItems.map((item) => item.itemCode);
    const allSourceData = await prodgeneratorService.getAllSourceDataByItemCodes(itemCodes);

    for (const addedItem of sourceItems) {
      const sourceObj = allSourceData.find((data) => {
        if (data?.sourceData?.[0]?.rootItemCode === addedItem.itemCode) return data;
        else return false;
      });

      const sourceData = sourceObj?.sourceData || [];

      if (
        !addedItem?.qty ||
        !sourceData?.length ||
        sourceData?.[0]?.rootItemCode !== addedItem.itemCode ||
        !selectedRowIds.current.has(addedItem.generatedNum) ||
        unavailableSources.current.has(`${addedItem.docTypeId}/${addedItem.docId}/${addedItem.lineNum}`)
      )
        continue;

      const generatedProds = [];

      for (const source of sourceData) {
        const inProd = generatedProds.find(
          (prod) => prod.level === source.level - 1 && prod.articleNumber === source.parentItemCode
        );

        const prodQty =
          source.level === 1
            ? addedItem.doc_joker_num2 !== null
              ? parseFloat(addedItem.doc_joker_num2)
              : parseFloat(addedItem.qty)
            : multiply(
                inProd?.productionQuantity ?? 1,
                divide(source.neededQuantity ?? 1, source.rootBaseQuantity ?? 1)
              );

        const reference = [addedItem.reference, addedItem.itemReference].filter(Boolean).join(' - ');

        const sd = {
          baseId: addedItem.docId,
          baseLine: addedItem.lineNum,
          baseDocNum: addedItem.docNum,
          baseDocType: addedItem.docTypeId,
          companyId: addedItem.companyId,
          endDate: addedItem.dueDate,
          articleNumber: source.relatedItemCode,
          joker_int1: source.sourceList?.joker_int1,
          joker_int2: source.sourceList?.joker_int2,
          joker_int3: source.sourceList?.joker_int3,
          joker_int4: source.sourceList?.joker_int4,
          joker_int5: source.sourceList?.joker_int5,
          joker_int6: source.sourceList?.joker_int6,
          joker_int7: source.sourceList?.joker_int7,
          joker_char1: addedItem.doc_joker_char3 ?? source.sourceList?.joker_char1,
          joker_char2: source.sourceList?.joker_char2,
          joker_char3: source.sourceList?.joker_char3,
          joker_char4: source.sourceList?.joker_char4,
          joker_char5: source.sourceList?.joker_char5,
          joker_char6: source.sourceList?.joker_char6,
          joker_char7: source.sourceList?.joker_char7,
          joker_char8: source.sourceList?.joker_char8,
          joker_char9: source.sourceList?.joker_char9,
          joker_char10: source.sourceList?.joker_char10,
          joker_char11: source.sourceList?.joker_char11,
          joker_char12: source.sourceList?.joker_char12,
          joker_char13: source.sourceList?.joker_char13,
          joker_char14: source.sourceList?.joker_char14,
          joker_char15: source.sourceList?.joker_char15,
          joker_char16: source.sourceList?.joker_char16,
          joker_char17: source.sourceList?.joker_char17,
          joker_char18: source.sourceList?.joker_char18,
          joker_char19: source.sourceList?.joker_char19,
          joker_char20: source.sourceList?.joker_char20,
          joker_char21: source.sourceList?.joker_char21,
          joker_char22: source.sourceList?.joker_char22,
          joker_char23: source.sourceList?.joker_char23,
          joker_char24: source.sourceList?.joker_char24,
          joker_char25: source.sourceList?.joker_char25,
          joker_num1: source.sourceList?.joker_num1,
          joker_num2: source.sourceList?.joker_num2,
          joker_num3: source.sourceList?.joker_num3,
          joker_num4: source.sourceList?.joker_num4,
          joker_num5: source.sourceList?.joker_num5,
          articleName: source.relatedItemName,
          productionQuantity: prodQty,
          projectId: addedItem.projectId,
          me: source.reletedQtyUnitId,
          description: addedItem.internalNote,
          reference,
          finishedItemCode: source.rootItemCode,
          level: source.level,
          whsCode: source.sourceList?.whsCode,
          binId: source.sourceList?.binId,
          productionOperation:
            source.sourceList?.sourceListOperation?.map((o) => {
              const baseQty = multiply(o.sourceListOperationMachine?.[0].neededQuantity, prodQty);
              return {
                operationId: o.operationId,
                quantityUnitId: o.sourceListOperationMachine[0].quantityUnitId,
                resourceOneId: o.sourceListOperationMachine?.[0].resourceId,
                baseRate: o.sourceListOperationMachine?.[0].neededQuantity,
                visOrder: o.visOrder,
                description: o.description,
                fixedLeadTime: o.fixedLeadTime,
                baseQty: baseQty,
                plannedQty: add(baseQty, o.fixedLeadTime ?? 0),
                dependence: o.dependence,
              };
            }) ?? [],
          productionItem:
            source.sourceList?.sourceListItem?.map((i) => ({
              itemCode: i.itemCode,
              quantity: multiply(i.neededQuantity, prodQty),
              baseRate: i.neededQuantity,
              joker_char1: i.joker_char1,
              joker_num1: i.joker_num1,
              joker_num2: i.joker_num2,
              description: i.description,
              issueMethod: i.issueMethod,
              whsCode: i.whsCode,
              secondaryProduct: i.secondaryProduct,
            })) ?? [],
        };

        generatedProds.push(sd);
      }

      setNewProds((previous) => previous.some((row) => sourceKey(row) === `${addedItem.docTypeId}/${addedItem.docId}/${addedItem.lineNum}`)
        || unavailableSources.current.has(`${addedItem.docTypeId}/${addedItem.docId}/${addedItem.lineNum}`)
        ? previous : [...previous, ...generatedProds]);
      setTreeData(allSourceData?.[allSourceData.length - 1]?.treeView ?? []);
      setSourceData(allSourceData?.[allSourceData.length - 1]?.sourceData);
      clearEditRows();
    }
    } catch {
      // The shared request handler displays errors from source-list requests.
    } finally {
      sourceItems.forEach((item) => buildingSources.current.delete(`${item.docTypeId}/${item.docId}/${item.lineNum}`));
      finishLoading();
    }
  };

  useEffect(() => {
    createRows();
  }, [selectedItems]);

  const handleProdQtyChange = (newValue, row) => {
    if (row.processingGroup || submitting.current) return;
    const newQty = parseFloat(newValue) || 0;
    setNewProds((prev) => {
      const index = prev.findIndex(
        (prod) =>
          prod.baseId === row.baseId &&
          prod.baseLine === row.baseLine &&
          prod.baseDocType === row.baseDocType &&
          prod.articleNumber === row.articleNumber
      );

      if (index === -1) return prev;

      const updatedProd = {
        ...prev[index],
        productionQuantity: newQty,
        productionOperation: prev[index].productionOperation?.map((o) => ({
          ...o,
          plannedQty: multiply(o.baseRate, newQty),
        })),
        productionItem: prev[index].productionItem?.map((i) => ({
          ...i,
          quantity: multiply(i.baseRate, newQty),
        })),
      };

      const updatedProds = [...prev];
      updatedProds[index] = updatedProd;

      return updatedProds;
    });
  };

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'baseDocNum', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (Object.keys(user).length) {
      setLoading(false);
    }
  }, [user]);

  const columns = useMemo(() => {
    return dataGridPremiumColumns([
      {
        field: 'processingStatus',
        headerName: 'Feldolgozási státusz',
        type: 'string',
        valueGetter: (_value, row) => productionGeneratorGroupStatuses[row.processingGroup?.status] ?? 'Beküldésre vár',
      },
      {
        field: 'processingError',
        headerName: 'Hiba oka',
        type: 'string',
        valueGetter: (_value, row) => row.processingGroup?.errorMessage ?? '',
        renderCell: ({ row }) => <span title={row.processingGroup?.errorMessage ?? ''}>{row.processingGroup?.errorMessage ?? ''}</span>,
      },
      {
        field: 'baseDocNum',
        headerName: 'Vere',
        type: 'string',
      },
      {
        field: 'baseLine',
        headerName: 'Sor',
        type: 'string',
      },
      {
        field: 'finishedItemCode',
        headerName: 'Késztermék',
        type: 'string',
      },
      {
        field: 'articleNumber',
        headerName: 'Cikk',
        type: 'string',
      },
      {
        field: 'articleName',
        headerName: 'Megnevezés',
        type: 'string',
      },
      {
        field: 'productionQuantity',
        headerName: 'Mennyiség',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="productionQuantity"
              type="number"
              variant="standard"
              fullWidth
              inputProps={{ min: 0.01, step: 'any' }}
              value={data.row.productionQuantity}
              onChange={(e) => {
                const val = e.target.value;
                const newValue = val === '' ? '' : parseFloat(val);

                if (val === '' || (newValue >= 0 && !isNaN(newValue))) {
                  handleProdQtyChange(newValue, data.row);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newValue,
                  });
                }
              }}
            />
          );
        },
      },
      /*{
        //@TODO
        field: 'me',
        headerName: 'ME',
        type: 'string',
      },*/
      {
        field: 'level',
        headerName: 'Szint',
        type: 'string',
      },
      {
        field: 'projectId',
        headerName: 'Projekt',
        type: 'string',
      },

      {
        field: 'description',
        headerName: 'Leírás',
        type: 'string',
      },
      {
        field: 'reference',
        headerName: 'Referencia',
        type: 'string',
      },
    ]);
  }, []);

  const CustomToolbar = ({ setShowConfirmDialog }) => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <Button
          size="small"
          sx={{ ml: 'auto' }}
          startIcon={<FactoryIcon />}
          onClick={() => {
            setShowConfirmDialog(true);
          }}
          disabled={draftProds.length === 0 || groupSubmitting}
        >
          Generálás
        </Button>
      </GridToolbarContainer>
    );
  };

  const handleGenClick = async () => {
    if (submitting.current || !draftProds.length) return;
    submitting.current = true;
    setGroupSubmitting(true);
    showLoader();
    try {
      const submitted = draftProds;
      const payload = JSON.stringify(submitted);
      if (submission.current?.payload !== payload) submission.current = { id: uuidv4(), payload };
      const result = await prodgeneratorService.enqueueGroups(submission.current.id, submitted);
      queueRevision.current += 1;
      result.groups.forEach((group) => lastPendingGroups.current.set(group.id, group));
      const groupMap = new Map(result.groups.map((group) => [sourceKey(group), group]));
      result.groups.forEach((group) => groupPayloadCache.current.set(group.id, submitted.filter((row) => sourceKey(row) === sourceKey(group))));
      setPendingProds((previous) => [
        ...previous,
        ...submitted.map((row) => ({
          ...row, processingGroup: { ...groupMap.get(sourceKey(row)), status: 'WAITING' },
        })),
      ]);
      const submittedSources = new Set(submitted.map(sourceKey));
      setGroupSourceState((previous) => ({
        ready: true, sourceKeys: new Set([...previous.sourceKeys, ...submittedSources]),
      }));
      setNewProds((previous) => previous.filter((row) => !submittedSources.has(sourceKey(row))));
      setRemovedItem(null);
      setSelectedItems([]);
      setSelectedRows([]);
      setPollRevision((value) => value + 1);
      addNotification({ content: 'A sorok beküldve. A feldolgozás állapota a táblázatban követhető.', type: NotificationType.INFO });
    } catch {
      // Keep the edited rows and submission ID for a safe retry.
    } finally {
      submitting.current = false;
      setGroupSubmitting(false);
      hideLoader();
    }
  };

  return (
    <>
      {pollError && <Alert severity="error" action={<Button onClick={() => setPollRevision((value) => value + 1)}>Frissítés</Button>}>
        A feldolgozási állapot frissítése sikertelen. A backend feldolgozása ettől még folytatódhat.
      </Alert>}
      {!loading && (
        <DatagridPremiumUi
          apiRef={apiRef}
          datagridIdentifier={DataGridIdentifiers.ProductionGeneratorNewProdTable}
          datagridInitialState={initialState}
          height={400}
          rows={rows}
          sx={productionGeneratorGridSx}
          getRowClassName={({ row }) => row.processingGroup ? `processing-${row.processingGroup.status}` : ''}
          isCellEditable={({ row }) => !row.processingGroup && !groupSubmitting}
          columns={[...columns, {
            field: 'processingActions', headerName: 'Műveletek', width: 150, sortable: false, filterable: false,
            renderCell: ({ row }) => ['FAILED', 'INTERRUPTED'].includes(row.processingGroup?.status)
              ? <Button size="small" disabled={Boolean(retrying)} onClick={() => retryGroup(row.processingGroup.id)}>Újraindítás</Button>
              : null,
          }]}
          paginationAndfilterMode="client"
          getRowId={(row) => `${row.baseId}_${row.baseLine}_${row.baseDocType}_${row.articleNumber}`}
          disableRowSelectionOnClick
          slots={{
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              newProds,
              setShowConfirmDialog,
            },
          }}
          editMode="row"
          onCellDoubleClick={(_params, event) => {
            event.defaultMuiPrevented = true;
          }}
          onCellClick={(params, event) => {
            const { row } = params;
            if (row.processingGroup || submitting.current) return;
            const generatedRowId = `${row.baseId}_${row.baseLine}_${row.baseDocType}_${row.articleNumber}`;

            const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

            for (const rowId of rowsInEditMode) {
              if (generatedRowId !== rowId) {
                apiRef.current.stopRowEditMode({ id: rowId });
              }
            }
            const rowMode = apiRef.current.getRowMode(generatedRowId);
            if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: generatedRowId });
          }}
        />
      )}

      <ConfirmDialogUI
        handleChangeElement={() => {
          handleGenClick();
          setShowConfirmDialog(false);
        }}
        show={showConfirmDialog}
        onHide={() => {
          setShowConfirmDialog(false);
        }}
        message={`Elkészíti a ${draftProds.length} gyártási utasítást?`}
      />
    </>
  );
};

export default ProductionGeneratorGroupNewProdTable;
